import { useEffect, useRef, useState } from 'react';

import { URLS } from '@/constants/urls';
import { YOUTUBE_PLAYER_READY_TIMEOUT_MS } from '@/constants/videoTutorials';
import VIDEO_TUTORIAL_STATUS from '@/enums/videoTutorialStatus';
import loadYouTubeIframeApi, {
  resetYouTubeIframeApi,
} from '@/utils/functions/loadYouTubeIframeApi/loadYouTubeIframeApi';

import type { TutorielVideo } from '@/types/videoTutorials';

interface UseVideoTutorialPlayerOptions {
  autoStart?: boolean;
  onPlay?: (tutorial: TutorielVideo) => void;
}

/**
 * Machine à états du lecteur d'un tutoriel vidéo : IDLE (vignette) → PLAYING (iframe) → ENDED (« Revoir ») ou ERROR (« Réessayer »).
 *
 * Ce n'est pas une requête métier : aucun appel à nos API. Le hook construit l'URL d'embed `youtube-nocookie`,
 * attache l'IFrame API YouTube à l'iframe rendue par React pour détecter la fin et les erreurs, et émet `onPlay` (tracking).
 *
 * Chaque lancement remonte l'iframe sous une nouvelle clé : les callbacks d'une iframe précédente sont ignorés.
 */
const useVideoTutorialPlayer = (
  tutorial: TutorielVideo,
  { autoStart = false, onPlay }: UseVideoTutorialPlayerOptions = {}
) => {
  const [status, setStatus] = useState<VIDEO_TUTORIAL_STATUS>(
    autoStart ? VIDEO_TUTORIAL_STATUS.PLAYING : VIDEO_TUTORIAL_STATUS.IDLE
  );
  const [iframeKey, setIframeKey] = useState(0);
  const iframeKeyRef = useRef(0);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const hasAutoStartedRef = useRef(false);
  const readyTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const loadedIframeKeyRef = useRef<number>(undefined);
  // Lecteur conservé pour ses événements (fin, erreur) : jamais détruit, voir handleIframeLoad.
  const playerRef = useRef<unknown>(undefined);

  // Une iframe remontée (Revoir, Réessayer) ne doit pas subir les callbacks de la précédente.
  const failIfCurrent = (key: number) => {
    if (key === iframeKeyRef.current) setStatus(VIDEO_TUTORIAL_STATUS.ERROR);
  };

  const preloadApi = () => {
    const key = iframeKeyRef.current;
    loadYouTubeIframeApi().catch(() => failIfCurrent(key));
  };

  const start = (shouldNotifyPlay: boolean) => {
    iframeKeyRef.current += 1;
    setIframeKey(iframeKeyRef.current);
    setStatus(VIDEO_TUTORIAL_STATUS.PLAYING);
    if (shouldNotifyPlay) onPlay?.(tutorial);
    preloadApi();
  };

  // Le ref survit au double effet de StrictMode : onPlay (tracking) n'est émis qu'une fois.
  useEffect(() => {
    if (!autoStart || hasAutoStartedRef.current) return;
    hasAutoStartedRef.current = true;
    onPlay?.(tutorial);
    preloadApi();
  }, []);

  // Garde jusqu'à onReady, armée dans un effet plutôt que dans start() : nettoyée à chaque sortie de PLAYING et au démontage.
  // Une iframe déjà chargée reste lisible sur réseau lent : le délai n'y abandonne que la détection de fin, sans erreur.
  // Le chargement de l'API est réinitialisé dans les deux cas (sans effet s'il a abouti) pour que les vidéos suivantes le relancent.
  useEffect(() => {
    if (status !== VIDEO_TUTORIAL_STATUS.PLAYING) return;
    const key = iframeKeyRef.current;
    const timeout = setTimeout(() => {
      resetYouTubeIframeApi();
      if (loadedIframeKeyRef.current === key) return;
      failIfCurrent(key);
    }, YOUTUBE_PLAYER_READY_TIMEOUT_MS);
    readyTimeoutRef.current = timeout;
    return () => clearTimeout(timeout);
  }, [status, iframeKey]);

  const play = () => start(true);

  const retry = () => start(false);

  const handleIframeLoad = () => {
    const key = iframeKeyRef.current;
    const iframe = iframeRef.current;
    loadedIframeKeyRef.current = key;
    loadYouTubeIframeApi()
      .then((youTube) => {
        if (key !== iframeKeyRef.current || !iframe?.isConnected) return;
        // Ne jamais appeler player.destroy() : il retirerait du DOM une iframe gérée par React.
        playerRef.current = new youTube.Player(iframe, {
          events: {
            onReady: () => {
              if (key === iframeKeyRef.current) clearTimeout(readyTimeoutRef.current);
            },
            onStateChange: (event) => {
              if (key === iframeKeyRef.current && event.data === youTube.PlayerState.ENDED)
                setStatus(VIDEO_TUTORIAL_STATUS.ENDED);
            },
            onError: () => failIfCurrent(key),
          },
        });
      })
      .catch(() => failIfCurrent(key));
  };

  const iframeSrc = `${URLS.YOUTUBE_NOCOOKIE_EMBED_URL}${encodeURIComponent(tutorial.videoId)}?enablejsapi=1&rel=0&autoplay=1&origin=${encodeURIComponent(globalThis.location.origin)}`;

  return { status, iframeKey, iframeSrc, iframeRef, play, retry, handleIframeLoad };
};

export default useVideoTutorialPlayer;
